import { RackConfiguration } from "./types"
import { PricingContext, calculatePrice, ZERO_PRICING } from "./pricing"

export interface CantileverBOMItem {
  materialId: string
  description: string
  category: string
  unit: string
  quantity: number
  unitPrice: number
  totalPrice: number
  weight: number
  totalWeight: number
  loadPerArm?: number
}

export interface CantileverBOMSummary {
  items: CantileverBOMItem[]
  totalPrice: number
  totalWeight: number
  maxLoadPerArm: number
}

export interface CantileverMaterialData {
  material_id: string
  description: string
  category: string
  unit: string
  column_type: string | null
  arm_type: string | null
  arm_length_mm: number | null
  column_height_mm: number | null
  coating: string | null
  load_per_arm_kg: number | null
  price: number
  weight_kg: number
}

// Calculate arm load capacity from materials DB
export function getArmLoadCapacity(
  materials: CantileverMaterialData[],
  config: RackConfiguration
): number {
  const arm = materials.find(
    (m) =>
      m.category === "Cantilever Arm" &&
      m.arm_type === config.armType &&
      m.arm_length_mm === config.armLength &&
      m.coating === config.armCoating
  )
  return arm?.load_per_arm_kg ?? 0
}

export function calculateCantileverBOM(
  materials: CantileverMaterialData[],
  config: RackConfiguration,
  pricingContext: PricingContext = ZERO_PRICING
): CantileverBOMSummary {
  const items: CantileverBOMItem[] = []
  let totalPrice = 0
  let totalWeight = 0

  const priced = (base: number) => calculatePrice(base, pricingContext)
  const isDouble = config.rackType === "double"

  // Columns (uprights) — one per bay
  const columnMat = materials.find(
    (m) =>
      m.category === "Cantilever Column" &&
      m.column_type === config.columnType &&
      m.column_height_mm === config.columnHeight &&
      m.coating === config.columnCoating
  )
  if (columnMat) {
    const qty = config.numberOfBays
    const up = priced(columnMat.price)
    items.push({
      materialId: columnMat.material_id,
      description: columnMat.description,
      category: columnMat.category,
      unit: columnMat.unit,
      quantity: qty,
      unitPrice: up,
      totalPrice: up * qty,
      weight: columnMat.weight_kg,
      totalWeight: columnMat.weight_kg * qty,
    })
    totalPrice += up * qty
    totalWeight += columnMat.weight_kg * qty
  }

  // Base profiles — one per bay
  const baseMat = materials.find(
    (m) =>
      m.category === "Cantilever Base" &&
      m.column_type === config.columnType &&
      m.coating === config.columnCoating
  )
  if (baseMat) {
    const qty = config.numberOfBays
    const up = priced(baseMat.price)
    items.push({
      materialId: baseMat.material_id,
      description: baseMat.description,
      category: baseMat.category,
      unit: baseMat.unit,
      quantity: qty,
      unitPrice: up,
      totalPrice: up * qty,
      weight: baseMat.weight_kg,
      totalWeight: baseMat.weight_kg * qty,
    })
    totalPrice += up * qty
    totalWeight += baseMat.weight_kg * qty
  }

  // Arms — numberOfBays × numberOfArmLevels × sides
  const armMat = materials.find(
    (m) =>
      m.category === "Cantilever Arm" &&
      m.arm_type === config.armType &&
      m.arm_length_mm === config.armLength &&
      m.coating === config.armCoating
  )
  if (armMat) {
    const sidesMultiplier = isDouble ? 2 : 1
    const qty = config.numberOfBays * config.numberOfArmLevels * sidesMultiplier
    const up = priced(armMat.price)
    items.push({
      materialId: armMat.material_id,
      description: armMat.description,
      category: armMat.category,
      unit: armMat.unit,
      quantity: qty,
      unitPrice: up,
      totalPrice: up * qty,
      weight: armMat.weight_kg,
      totalWeight: armMat.weight_kg * qty,
      loadPerArm: armMat.load_per_arm_kg ?? undefined,
    })
    totalPrice += up * qty
    totalWeight += armMat.weight_kg * qty
  }

  // Horizontal ties (RHS) — between each pair of bays, top + bottom + intermediates
  const rhsMat = materials.find((m) => m.category === "Cantilever RHS Tie")
  if (rhsMat && config.numberOfBays > 1) {
    // Calculate intermediate levels same as renderer
    const columnHeightM = config.columnHeight / 1000
    const bottomTieY = 0.3
    const topTieY = columnHeightM - 0.3
    const braceHeight = topTieY - bottomTieY
    const maxCrossHeight = 2.5
    const numberOfCrosses = Math.ceil(braceHeight / maxCrossHeight)
    const intermediateCount = numberOfCrosses - 1

    const tiesPerConnection = 2 + intermediateCount
    const qty = (config.numberOfBays - 1) * tiesPerConnection
    const up = priced(rhsMat.price)
    items.push({
      materialId: rhsMat.material_id,
      description: rhsMat.description,
      category: rhsMat.category,
      unit: rhsMat.unit,
      quantity: qty,
      unitPrice: up,
      totalPrice: up * qty,
      weight: rhsMat.weight_kg,
      totalWeight: rhsMat.weight_kg * qty,
    })
    totalPrice += up * qty
    totalWeight += rhsMat.weight_kg * qty
  }

  // Vertical brace sets
  const braceMat = materials.find((m) => m.category === "Cantilever Brace Cross")
  if (braceMat && config.numberOfBays > 1) {
    let braceCount = 0
    for (let i = 0; i < config.numberOfBays - 1; i++) {
      if (i === 0 || (i + 1) % 5 === 0) braceCount++
    }
    if (braceCount > 0) {
      const up = priced(braceMat.price)
      items.push({
        materialId: braceMat.material_id,
        description: braceMat.description,
        category: braceMat.category,
        unit: braceMat.unit,
        quantity: braceCount,
        unitPrice: up,
        totalPrice: up * braceCount,
        weight: braceMat.weight_kg,
        totalWeight: braceMat.weight_kg * braceCount,
      })
      totalPrice += up * braceCount
      totalWeight += braceMat.weight_kg * braceCount
    }
  }

  const maxLoadPerArm = armMat?.load_per_arm_kg ?? 0

  return {
    items,
    totalPrice: Math.round(totalPrice * 100) / 100,
    totalWeight: Math.round(totalWeight * 100) / 100,
    maxLoadPerArm,
  }
}
